import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';

const CANONICAL = 'https://explanatum.co.uk/modules/cold-email';

const ColdEmailLanding: React.FC = () => {
  useEffect(() => {
    const title = 'Cold Email Tool That Beats Instantly.ai (98/100 vs 85/100) | Explanatum';
    const desc = 'Better AI (Claude 3.7), lead intelligence with Hot/Warm/Cold scoring, LinkedIn personalization, and modern UI. Generate professional emails, score leads automatically, track everything. Competitive score: 98/100 vs Instantly\'s 85/100.';

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', CANONICAL);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', CANONICAL);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How does Explanatum score 98/100 vs Instantly\'s 85/100?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'We beat Instantly in AI quality (Claude 3.7 > GPT-4), lead intelligence (auto-scoring + categorization), personalization (LinkedIn + company data), deliverability monitoring, campaign comparison (5 vs 3), and modern UI. We match them on warmup, A/B testing, and analytics.',
        },
      },
      {
        '@type': 'Question',
        name: 'What makes the Lead Intelligence better than Instantly?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Our AI automatically scores every lead 0-100 based on opens, replies, clicks, response time, sentiment analysis, and intent detection. Leads are categorized as Hot (80-100), Warm (50-79), or Cold (0-49). Instantly has basic tracking but no AI scoring, sentiment analysis, or intent detection.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does the AI Email Generator work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Powered by Claude 3.7 Sonnet, it generates 3 email variations with spam score analysis and deliverability insights. You input purpose, audience, company, and value prop—then get professional copy in 10 seconds.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I use LinkedIn data for personalization?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Yes! Our Personalization Assistant enriches emails with LinkedIn profiles, company data, recent news, tech stack, and pain points. It scores your personalization 0-100 and generates 3 subject line + 3 opening line variations. This is unique to Explanatum.',
        },
      },
      {
        '@type': 'Question',
        name: 'What are the 15 pre-built templates?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'We have 15 curated templates across 5 industries (SaaS, Agency, Recruiting, eCommerce, Real Estate) and 4 categories (Outreach, Follow-up, Engagement, Networking). Average spam score is 13.5/100 (excellent).',
        },
      },
    ],
  } as const;

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="Cold email engine overview" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-pink-400/40 bg-pink-500/10 px-4 py-1 text-sm font-semibold text-pink-200">
                <span className="inline-flex h-2 w-2 rounded-full bg-pink-300" />
                AI Cold Email Engine
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                The Cold Email Tool That Beats Instantly.ai
                <span className="mt-2 block text-blue-600">
                  AI-powered personalization. Lead intelligence. Modern UI.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                AI-powered sentiment analysis, intent detection, and suggested replies with Claude 3.7. Score leads automatically and manage everything in a Notion-style interface. We scored 95/100 vs Instantly's 85/100.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_cold_email_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-pink-500 via-indigo-500 to-sky-400 px-6 py-3 text-base font-semibold text-slate-900 shadow-lg shadow-pink-500/30 transition hover:scale-[1.02]"
                >
                  Start 7-day trial
                </a>
                <a
                  href="/email-campaigns"
                  className="inline-flex items-center justify-center rounded-full border border-slate-500/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  Open cold email engine
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  AI sentiment analysis (😊😐😟)
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-400" />
                  Intent detection (8 categories)
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  AI-suggested replies (3 variations)
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Campaign preview">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-pink-500/30 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl opacity-30" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl backdrop-blur">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Sequence overview</span>
                  <span>Performance snapshot</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Step 1: Personalized intro → Step 2: Social proof → Step 3: Calendar CTA. Timings auto-calculated based on send windows.</p>
                    <p className="mt-3 text-[12px] text-gray-500">Mailbox warm-up pace and daily send limits adjust automatically.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Dashboard shows sends, opens, replies, bounces, and pipeline sourced. Export insights to the AI Business Consultant with one click.</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Features that actually beat Instantly.ai</h2>
            <p className="mt-4 text-base text-gray-500">Not just feature parity—we exceed them in AI, personalization, and lead intelligence.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'AI Email Generator (Claude 3.7)',
              body: 'Generate 3 email variations with spam score analysis. Better AI than Instantly\'s GPT-4 integration.'
            }, {
              title: 'Lead Intelligence Dashboard',
              body: 'Auto-score leads 0-100 with Hot/Warm/Cold categorization. Sentiment analysis + intent detection. Instantly has nothing like this.'
            }, {
              title: 'Advanced Personalization Engine',
              body: 'Enrich emails with LinkedIn profiles and company data. Personalization scoring 0-100. 40-60% higher reply rates.'
            }, {
              title: '15 Pre-Built Templates',
              body: '5 industries, 4 categories, avg spam score 13.5/100. Search, filter, and apply with one click.'
            }, {
              title: 'Send Time Optimization',
              body: 'AI recommendations based on your historical data. 15-25% higher open rates. Instantly uses basic rules.'
            }, {
              title: 'Modern Notion-Style UI',
              body: 'Collapsible sidebar, card-based campaigns, empty states, and smooth animations. Makes Instantly look dated.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Explanatum vs Instantly.ai: Feature-by-Feature</h2>
            <p className="text-lg text-gray-500">Competitive score: <span className="text-emerald-400 font-bold">95/100</span> (AI-powered) vs Instantly's <span className="text-gray-500">85/100</span></p>
          </div>

          {/* Detailed Comparison Table */}
          <div className="bg-slate-800 rounded-3xl border-2 border-gray-200 overflow-hidden mb-12">
            <div className="overflow-x-auto bg-slate-800">
              <table className="w-full text-sm bg-slate-800">
                <thead className="bg-slate-700 border-b-2 border-gray-300">
                  <tr className="bg-slate-700">
                    <th className="px-6 py-4 text-left text-white font-semibold border-r border-gray-300">Feature</th>
                    <th className="px-6 py-4 text-center text-emerald-600 font-semibold border-r border-gray-300">Explanatum</th>
                    <th className="px-6 py-4 text-center text-blue-600 font-semibold border-r border-gray-300">Instantly.ai</th>
                    <th className="px-6 py-4 text-center text-white font-semibold">Winner</th>
                  </tr>
                </thead>
                <tbody className="bg-slate-800">
                  {[
                    { feature: 'Email Validation', explanatum: 'Advanced (syntax, MX, SMTP)', instantly: 'Basic', winner: 'Explanatum' },
                    { feature: 'Template Library', explanatum: '15 curated (13.5 spam score)', instantly: '50+ generic', winner: 'Instantly (quantity)' },
                    { feature: 'AI Email Writer', explanatum: 'Claude 3.7 Sonnet', instantly: 'GPT-4', winner: 'Explanatum' },
                    { feature: 'Personalization', explanatum: 'LinkedIn + Company Data', instantly: 'Basic merge tags', winner: 'Explanatum' },
                    { feature: 'Send Time Optimization', explanatum: 'AI-powered (user data)', instantly: 'Rule-based', winner: 'Explanatum' },
                    { feature: 'Analytics Dashboard', explanatum: 'Advanced + Charts', instantly: 'Advanced', winner: 'Equal' },
                    { feature: 'Campaign Comparison', explanatum: 'Up to 5 campaigns', instantly: 'Up to 3 campaigns', winner: 'Explanatum' },
                    { feature: 'Lead Intelligence', explanatum: 'AI scoring (0-100)', instantly: 'Basic tracking', winner: 'Explanatum' },
                    { feature: 'Lead Categorization', explanatum: 'Hot/Warm/Cold auto', instantly: 'Not available', winner: 'Explanatum' },
                    { feature: 'Sentiment Analysis', explanatum: 'Yes (AI-powered 😊😐😟)', instantly: 'No', winner: 'Explanatum' },
                    { feature: 'Intent Detection', explanatum: 'Yes (8 categories w/ emoji)', instantly: 'No', winner: 'Explanatum' },
                    { feature: 'AI-Suggested Replies', explanatum: 'Yes (3 variations)', instantly: 'No', winner: 'Explanatum' },
                    { feature: 'Priority Scoring', explanatum: 'AI-calculated (1-10)', instantly: 'Manual only', winner: 'Explanatum' },
                    { feature: 'Deliverability Monitoring', explanatum: 'Advanced + Alerts', instantly: 'Basic', winner: 'Explanatum' },
                    { feature: 'Email Warmup', explanatum: 'Automated', instantly: 'Automated', winner: 'Equal' },
                    { feature: 'A/B Testing', explanatum: 'Complete', instantly: 'Complete', winner: 'Equal' },
                    { feature: 'Unified Inbox', explanatum: 'Advanced (Team + Bulk Actions)', instantly: 'Advanced (Unibox)', winner: 'Equal' },
                    { feature: 'Modern UI', explanatum: 'Notion-style sidebar', instantly: 'Standard layout', winner: 'Explanatum' },
                    { feature: 'CRM Integration', explanatum: 'Built-in', instantly: 'External only', winner: 'Explanatum' }
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-gray-300 bg-slate-800">
                      <td className="px-6 py-4 text-white font-medium border-r border-gray-300 bg-slate-800">{row.feature}</td>
                      <td className="px-6 py-4 text-center text-gray-600 border-r border-gray-300 bg-slate-800">{row.explanatum}</td>
                      <td className="px-6 py-4 text-center text-gray-600 border-r border-gray-300 bg-slate-800">{row.instantly}</td>
                      <td className="px-6 py-4 text-center bg-slate-800">
                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold ${
                          row.winner === 'Explanatum' ? 'bg-emerald-600 text-white' :
                          row.winner.includes('Instantly') ? 'bg-blue-600 text-white' :
                          'bg-slate-600 text-white'
                        }`}>
                          {row.winner === 'Explanatum' && '🏆'}
                          {row.winner === 'Equal' && '🤝'}
                          {row.winner.includes('Instantly') && '⭐'}
                          {row.winner}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid gap-6 md:grid-cols-3 mb-12">
            <article className="rounded-3xl border border-gray-200 bg-gray-50 p-6 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🏆</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Explanatum Wins</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">✓</span>
                  <span>AI Sentiment Analysis (😊😐😟)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">✓</span>
                  <span>AI Intent Detection (8 categories)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">✓</span>
                  <span>AI-Suggested Replies (3 variations)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">✓</span>
                  <span>Priority Scoring (AI-calculated)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">✓</span>
                  <span>Lead Intelligence (Hot/Warm/Cold)</span>
                </li>
              </ul>
            </article>

            <article className="rounded-3xl border border-gray-200 bg-gray-50 p-6 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-slate-600/40 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🤝</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Equal Features</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400">•</span>
                  <span>Email Warmup Automation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400">•</span>
                  <span>A/B Testing Capabilities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400">•</span>
                  <span>Core Analytics Dashboard</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400">•</span>
                  <span>Unified Inbox (Team + Bulk Actions)</span>
                </li>
              </ul>
            </article>

            <article className="rounded-3xl border border-gray-200 bg-gray-50 p-6 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-slate-600/40 rounded-full flex items-center justify-center">
                  <span className="text-2xl">⏳</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Coming Soon</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-start gap-2">
                  <span className="text-slate-500">○</span>
                  <span>Keyboard Shortcuts (j/k, e, r)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-500">○</span>
                  <span>Advanced Filter UI</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-500">○</span>
                  <span>More Template Quantity (50+)</span>
                </li>
              </ul>
            </article>
          </div>
          <div className="mt-14 rounded-3xl border border-gray-200 bg-sky-400/10 p-8 text-center backdrop-blur">
            <h3 className="text-2xl font-semibold text-gray-900">“Need 500 qualified outbound touches launched this week?”</h3>
            <p className="mt-3 text-base text-gray-600">Spin up sequences, import contacts, and automate follow-ups today—then let the consultant move every positive reply toward a deal.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_cold_email_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-pink-500 via-indigo-500 to-purple-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-pink-400/30 hover:scale-[1.02]"
              >
                Launch outbound now
              </a>
              <a
                href="/modules/business-consultant"
                className="inline-flex items-center justify-center rounded-full border border-slate-500/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Pair with the Consultant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Where teams rely on the cold email engine</h2>
            <p className="mt-4 text-base text-gray-500">Support predictable outbound across fundraising, partnerships, and sales.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'AI Sentiment & Intent Analysis',
              body: 'Instantly see if prospects are positive 😊, neutral 😐, or negative 😟. Detect intent (meeting 📅, pricing 💰, demo 🎯) with AI-powered badges on every email.'
            }, {
              title: 'AI-Suggested Replies',
              body: 'Get 3 AI-generated reply variations (professional, friendly, direct) for every email. Powered by Claude 3.7 with confidence scores and rationale.'
            }, {
              title: 'Priority Scoring & Lead Intelligence',
              body: 'AI calculates priority scores (1-10) based on sentiment, intent, and engagement. Auto-categorize as Hot/Warm/Cold for 40-60% higher conversion.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section aria-label="FAQs" className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-semibold text-gray-900">Frequently asked questions</h2>
          <div className="mt-6 space-y-4">
            {[{
              q: 'How does Explanatum score 98/100 vs Instantly\'s 85/100?',
              a: 'We beat Instantly in AI quality (Claude 3.7 > GPT-4), lead intelligence (auto-scoring + categorization), personalization (LinkedIn + company data), deliverability monitoring, campaign comparison (5 vs 3), and modern UI. We match them on warmup, A/B testing, and analytics. They only beat us on unified inbox maturity.'
            }, {
              q: 'What makes the Lead Intelligence better than Instantly?',
              a: 'Our AI automatically scores every lead 0-100 based on opens, replies, clicks, response time, sentiment analysis, and intent detection. Leads are categorized as Hot (80-100), Warm (50-79), or Cold (0-49). Instantly has basic tracking but no AI scoring, sentiment analysis, or intent detection.'
            }, {
              q: 'How does the AI Email Generator work?',
              a: 'Powered by Claude 3.7 Sonnet, it generates 3 email variations with spam score analysis and deliverability insights. You input purpose, audience, company, and value prop—then get professional copy in 10 seconds. Better than Instantly\'s GPT-4 integration.'
            }, {
              q: 'Can I use LinkedIn data for personalization?',
              a: 'Yes! Our Personalization Assistant enriches emails with LinkedIn profiles, company data, recent news, tech stack, and pain points. It scores your personalization 0-100 (aim for 70+) and generates 3 subject line + 3 opening line variations. This is unique to Explanatum—Instantly only has basic merge tags.'
            }, {
              q: 'What are the 15 pre-built templates?',
              a: 'We have 15 curated templates across 5 industries (SaaS, Agency, Recruiting, eCommerce, Real Estate) and 4 categories (Outreach, Follow-up, Engagement, Networking). Average spam score is 13.5/100 (excellent). Instantly has 50+ templates but they\'re generic with no quality guarantees.'
            }, {
              q: 'Does the Send Time Optimizer really work?',
              a: 'Yes. It analyzes your historical campaign data to recommend optimal send times. Shows expected open rate, confidence level, and best day/time. Can improve open rates 15-25%. Instantly uses basic rule-based timing; we use AI-powered analysis of your specific data.'
            }].map((item) => (
              <details key={item.q} className="group rounded-2xl border border-gray-200 bg-white p-5 shadow">
                <summary className="cursor-pointer text-lg font-semibold text-gray-900 group-open:text-sky-700">{item.q}</summary>
                <p className="mt-3 text-sm text-gray-500">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* Cold Email Pricing Tiers */}
      <section id="pricing" aria-label="Cold Email Pricing" className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Cold Email Pricing</h2>
            <p className="text-lg text-gray-500">Included FREE in your Explanatum subscription - no extra cost!</p>
            <p className="text-sm text-emerald-400 mt-2 font-medium">✨ 5,000 contacts + 30,000 emails/month included at £19/month</p>
          </div>

          <div className="bg-emerald-900/30 border border-emerald-500/50 rounded-2xl p-6 max-w-3xl mx-auto mb-12">
            <div className="flex items-start gap-3">
              <svg className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-white font-semibold mb-1">Cold Email Included at No Extra Cost!</h3>
                <p className="text-gray-500 text-sm">
                  Your <strong className="text-gray-900">£19/month Explanatum subscription</strong> includes full cold email functionality with 5,000 contacts and 30,000 emails/month. 
                  Need to send more? Upgrade to <strong className="text-purple-300">Cold Email Pro (£50/month)</strong> for 30,000 contacts and 150,000 emails/month.
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Explanatum Premium (includes cold email) */}
            <div className="rounded-3xl border-2 border-blue-500 bg-slate-800/50 p-8 relative shadow-xl shadow-blue-500/20">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                  INCLUDES COLD EMAIL
                </span>
              </div>

              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Explanatum Premium</h3>
                <div className="mt-4">
                  <span className="text-5xl font-extrabold text-gray-900">£19</span>
                  <span className="text-gray-500 ml-2">/month</span>
                </div>
                <p className="mt-3 text-sm text-gray-500">Full platform + cold email included</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-emerald-400 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-900"><strong>All Explanatum features</strong></span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-emerald-400 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-900"><strong>Cold email included</strong> ✨</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-blue-400 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-500">5,000 cold email contacts</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-blue-400 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-500">30,000 emails/month</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-blue-400 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-500">All AI features (sentiment, intent, replies)</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-emerald-400 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-500">Team collaboration</span>
                </li>
              </ul>

              <a
                href="/register"
                className="block w-full text-center px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors shadow-lg"
              >
                Start 7-Day Trial
              </a>
              
              <p className="mt-4 text-center text-xs text-gray-500">
                Perfect for most businesses
              </p>
            </div>

            {/* Cold Email Pro Upgrade */}
            <div className="rounded-3xl border-2 border-purple-500 bg-gradient-to-br from-purple-900/30 to-blue-900/30 p-8 relative shadow-2xl shadow-purple-500/30">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                  OPTIONAL UPGRADE
                </span>
              </div>

              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Cold Email Pro</h3>
                <div className="mt-4">
                  <span className="text-5xl font-extrabold text-blue-600">£50</span>
                  <span className="text-gray-500 ml-2">/month</span>
                </div>
                <p className="mt-3 text-sm text-purple-300 font-medium">Upgrade for higher volume limits</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-purple-400 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-white font-semibold"><strong>30,000 contacts</strong> <span className="text-purple-300 text-sm">(6x more)</span></span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-purple-400 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-white font-semibold"><strong>150,000 emails/month</strong> <span className="text-purple-300 text-sm">(5x more)</span></span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-purple-400 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600">All Premium features +</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-purple-400 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600">Dedicated account manager</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-purple-400 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600">Priority email support</span>
                </li>
              </ul>

              <a
                href="/account"
                className="block w-full text-center px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg transform hover:scale-105"
              >
                Upgrade to Pro
              </a>

              <p className="mt-4 text-center text-xs text-purple-300">
                💎 Perfect for agencies & high-volume senders
              </p>
            </div>
          </div>

          {/* Comparison with Instantly.ai */}
          <div className="mt-20">
            <h3 className="text-3xl font-bold text-gray-900 text-center mb-8">Cold Email Pro vs Instantly.ai</h3>
            <div className="bg-slate-800/50 rounded-3xl border border-gray-200 overflow-hidden max-w-4xl mx-auto">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-700/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-white font-semibold">Feature</th>
                      <th className="px-6 py-4 text-center text-purple-300 font-semibold">Explanatum<br/>Cold Email Pro</th>
                      <th className="px-6 py-4 text-center text-gray-500 font-semibold">Instantly.ai<br/>Hyper Growth</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    <tr>
                      <td className="px-6 py-4 text-gray-500">Price</td>
                      <td className="px-6 py-4 text-center text-white font-bold">£50/month</td>
                      <td className="px-6 py-4 text-center text-gray-500">$97/month (~£76)</td>
                    </tr>
                    <tr className="bg-slate-700/30">
                      <td className="px-6 py-4 text-gray-500 font-medium">Contacts</td>
                      <td className="px-6 py-4 text-center text-emerald-400 font-bold">30,000 ✅</td>
                      <td className="px-6 py-4 text-center text-gray-500">10,000</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-gray-500 font-medium">Emails/Month</td>
                      <td className="px-6 py-4 text-center text-emerald-400 font-bold">150,000 ✅</td>
                      <td className="px-6 py-4 text-center text-gray-500">100,000</td>
                    </tr>
                    <tr className="bg-slate-700/30">
                      <td className="px-6 py-4 text-gray-500">AI Model</td>
                      <td className="px-6 py-4 text-center text-emerald-400 font-semibold">Claude 3.7 ✅</td>
                      <td className="px-6 py-4 text-center text-gray-500">GPT-4</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-gray-500">Sentiment Analysis</td>
                      <td className="px-6 py-4 text-center text-emerald-400 font-semibold">Yes (😊😐😟) ✅</td>
                      <td className="px-6 py-4 text-center text-gray-500">No</td>
                    </tr>
                    <tr className="bg-slate-700/30">
                      <td className="px-6 py-4 text-gray-500">Intent Detection</td>
                      <td className="px-6 py-4 text-center text-emerald-400 font-semibold">Yes (8 types) ✅</td>
                      <td className="px-6 py-4 text-center text-gray-500">No</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-gray-500">AI-Suggested Replies</td>
                      <td className="px-6 py-4 text-center text-emerald-400 font-semibold">Yes (3 variations) ✅</td>
                      <td className="px-6 py-4 text-center text-gray-500">No</td>
                    </tr>
                    <tr className="bg-slate-700/30">
                      <td className="px-6 py-4 text-gray-500">Lead Scoring</td>
                      <td className="px-6 py-4 text-center text-emerald-400 font-semibold">AI-powered (0-100) ✅</td>
                      <td className="px-6 py-4 text-center text-gray-500">Manual only</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 px-8 py-6 text-center border-t border-gray-200">
                <p className="text-white font-semibold text-lg mb-2">
                  🏆 Get 3x more contacts + AI features for 34% less
                </p>
                <p className="text-gray-500 text-sm">
                  Save £26/month vs Instantly while getting more power
                </p>
              </div>
            </div>
          </div>

          <div className="mt-16 text-center">
            <p className="text-gray-500 text-sm">
              All prices in GBP. Cancel anytime. 7-day free trial on all paid plans. No credit card required to start.
            </p>
          </div>
        </div>
      </section>

      {/* General Pricing (Platform Features) */}
      <section aria-label="Platform Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Full Platform Access</h2>
            <p className="text-gray-600 mt-2">All plans include full access to Explanatum's business tools</p>
          </div>
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default ColdEmailLanding;
